import { useState } from 'react';
import { useNavigate } from 'react-router';
import { mutateConfig } from '../api/mutationConfig';
import { UsersDropdown } from '../components/UsersDropdown';
import { ProgramsDropdown } from '../components/ProgramsDropdown';
import { TerminalsDropdown } from '../components/TerminalsDropdown';
import { TransactionType, TransactionCategory, formTransactionStatuses, formTransactionCategories } from '../constants/transaction';

interface TransactionForm {
  amount: number | '';
  type: string;
  status: string;
  category: string;
  narration: string;
  created_at: string;
  user_uid: string;
  program_uid: string;
  terminal_uid: string;
}


export const NewUserTransaction = () => {
  const navigate = useNavigate();
  const { mutate: createTransaction, isPending } = mutateConfig()<void>({
    url: '/v1/ops/user-transactions',
  });

  const [displayAmount, setDisplayAmount] = useState('');

  const [form, setForm] = useState<TransactionForm>({
    amount: '' as const,
    type: '',
    status: '',
    category: TransactionCategory.Regular,
    narration: '',
    created_at: '',
    user_uid: '',
    program_uid: '',
    terminal_uid: '',
  });

  const isValid =
    form.amount !== '' &&
    form.type !== '' &&
    form.status !== '' &&
    form.user_uid !== '';

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/[^0-9.]/g, '');
    const parts = raw.split('.');
    const integer = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const formatted = parts.length > 1 ? `${integer}.${parts[1]}` : integer;
    setDisplayAmount(formatted);
    setForm((prev) => ({ ...prev, amount: raw === '' ? '' : Number(raw) }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: { preventDefault(): void }) => {
    e.preventDefault();
    createTransaction(
      {
        amount: (form.amount as number) * 100,
        ...(form.terminal_uid && { terminal_uid: form.terminal_uid }),
        ...(form.created_at && { created_at: new Date(form.created_at).toISOString() }),
        type: form.type,
        status: form.status,
        category: form.category,
        ...(form.narration && { narration: form.narration }),
        user_uid: form.user_uid,
        ...(form.program_uid && { program_uid: form.program_uid }),
      },
      { onSuccess: () => navigate('/transactions') }
    );
  };

  return (
    <div className="flex flex-col gap-6 max-w-lg">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/transactions')}
          className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
        >
          ← Back
        </button>
        <h1 className="text-xl font-semibold text-gray-900">New User Transaction</h1>
      </div>
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">User</label>
            <UsersDropdown
              value={form.user_uid}
              onChange={(v) => setForm((prev) => ({ ...prev, user_uid: v }))}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Program</label>
            <ProgramsDropdown
              value={form.program_uid}
              onChange={(v) => setForm((prev) => ({ ...prev, program_uid: v, terminal_uid: '' }))}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Amount</label>
            <input
              name="amount"
              type="text"
              inputMode="decimal"
              value={displayAmount}
              onChange={handleAmountChange}
              placeholder="0"
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Type</label>
            <select
              name="type"
              value={form.type}
              onChange={handleChange}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              <option value="">Select a type</option>
              {Object.values(TransactionType).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Status</label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              <option value="">Select a status</option>
              {formTransactionStatuses.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Category</label>
            <select
              name="category"
              value={form.category}
              onChange={handleChange}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              {formTransactionCategories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              Narration <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              name="narration"
              type="text"
              value={form.narration}
              onChange={handleChange}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              Terminal <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <TerminalsDropdown
              programUid={form.program_uid}
              value={form.terminal_uid}
              onChange={(v) => setForm((prev) => ({ ...prev, terminal_uid: v }))}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">
              Date <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              name="created_at"
              type="datetime-local"
              value={form.created_at}
              onChange={handleChange}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>
          <button
            type="submit"
            disabled={!isValid || isPending}
            className="w-full py-2 px-4 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isPending ? 'Saving...' : 'Create Transaction'}
          </button>
        </form>
      </div>
    </div>
  );
};
